import React, { useCallback, useEffect } from 'react'
import styled from 'styled-components/macro'
import { PiGearFill, PiTrayFill } from 'react-icons/pi'
import { useLeftNavStore } from './left-nav-store'
import { SlideOutNavItem } from './SlideOutNavItem'
import { TargetsSlideOutContent } from './TargetsSlideOutContent'
import { TargetArrowIcon } from './nav-icons'
import type { NavSection } from './types'

// Section titles for the slide-out panel
const SECTION_TITLES: Record<NavSection, string> = {
  targets: 'Targets',
  campaigns: 'Campaigns',
  samples: 'Samples',
  rebates: 'Rebates',
  deals: 'Deals',
  performance: 'Performance',
  workspace: 'Workspace',
  settings: 'Settings',
}

// Section configurations for non-targets sections
const SECTION_ITEMS: Partial<
  Record<NavSection, Array<{ label: string; path: string }>>
> = {
  workspace: [
    { label: 'Documents', path: '/documents' },
    { label: 'Contacts', path: '/contacts' },
    { label: 'Activity', path: '/activity' },
  ],
  settings: [
    { label: 'General', path: '/settings/general' },
    { label: 'Team', path: '/settings/team' },
    { label: 'Products', path: '/settings/products' },
    { label: 'Integrations', path: '/settings/integrations' },
  ],
}

const PanelContainer = styled.aside<{ $isOpen: boolean }>`
  width: ${({ $isOpen }) => ($isOpen ? 'var(--slide-out-panel-width)' : '0')};
  min-width: ${({ $isOpen }) =>
    $isOpen ? 'var(--slide-out-panel-width)' : '0'};
  background: var(--slide-out-bg);
  transition:
    width 0.2s ease-in-out,
    min-width 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  border-radius: 8px 0 0 8px;
  overflow: hidden;
`

const PanelInner = styled.div`
  width: var(--slide-out-panel-width);
  display: flex;
  flex-direction: column;
  height: 100%;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px;
  opacity: 0.8;
`

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: #191d23;
  text-transform: uppercase;
  letter-spacing: 1px;
`

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: #191d23;

  svg {
    width: 16px;
    height: 16px;
  }
`

const Divider = styled.div`
  height: 1px;
  background: rgba(25, 29, 35, 0.15);
  margin: 0 12px 12px;
`

const PanelContent = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
`

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

interface SlideOutPanelProps {
  className?: string
  $isOpen?: boolean
}

export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({
  className,
  $isOpen = false,
}) => {
  const { isSlideOutOpen, activeSection, closeSlideOut } = useLeftNavStore()

  const title = activeSection ? SECTION_TITLES[activeSection] : ''
  const items = activeSection ? SECTION_ITEMS[activeSection] : undefined

  // Close on escape key
  useEffect(() => {
    if (!isSlideOutOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSlideOut()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isSlideOutOpen, closeSlideOut])

  // Handle click outside to close
  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement
      // Check if click is outside the panel and left nav
      if (
        !target.closest('[data-slide-out-panel]') &&
        !target.closest('[data-left-nav]')
      ) {
        closeSlideOut()
      }
    },
    [closeSlideOut]
  )

  useEffect(() => {
    if (!isSlideOutOpen) return

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSlideOutOpen, handleClickOutside])

  // Render the appropriate content based on active section
  const renderContent = () => {
    if (activeSection === 'targets') {
      return <TargetsSlideOutContent />
    }

    if (items && activeSection) {
      return (
        <NavList>
          {items.map((item) => (
            <SlideOutNavItem
              key={item.path}
              label={item.label}
              path={item.path}
              section={activeSection}
            />
          ))}
        </NavList>
      )
    }

    return null
  }

  return (
    <PanelContainer
      $isOpen={$isOpen}
      className={className}
      role="navigation"
      aria-label={`${title} navigation`}
      data-slide-out-panel
    >
      <PanelInner>
        <PanelHeader>
          <TitleIcon>
            {activeSection === 'targets' && <TargetArrowIcon size={16} />}
            {activeSection === 'workspace' && <PiTrayFill />}
            {activeSection === 'settings' && <PiGearFill />}
          </TitleIcon>
          <PanelTitle>{title}</PanelTitle>
        </PanelHeader>
        <Divider />
        <PanelContent>{renderContent()}</PanelContent>
      </PanelInner>
    </PanelContainer>
  )
}
